import React, { useMemo, useState } from "react";
import Styles from "./styles.module.scss";
import Logo from "../_UI/logo";
import CrossDomainLink from "../_UI/crossDomainLink";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LangSwitcher from "../_UI/langSwitcher";

import { logout } from "../../store/cabinet/action";

import logOutIcon from "../../theme/assets/svg/tc_log_out_icon.svg";
import settingsIcon from "../../theme/assets/svg/b_settings_icon.svg";

const WALLET_ROUTE = "/cabinet/wallet";

const CabinetHeader = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [isMobileNav, setIsMobileNav] = useState(false);

    const userData = useSelector(store => store.cabinet.userData);
    const logoutStatusStarted = useSelector(store => store.cabinet.logoutStatusStarted);
    const subscribeStatus = useSelector(store => store.cabinet.subscribeStatus);

    const isSubscriptionActive = subscribeStatus === "active";

    const userName = useMemo(() => {
        const firstName = userData?.firstName || userData?.first_name || "";
        const lastName = userData?.lastName || userData?.last_name || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return fullName || userData?.name || "Steven Johnson";
    }, [userData]);

    const avatarSrc = userData?.avatar_url || userData?.avatarUrl || userData?.photo || userData?.photoUrl;
    const avatarLetter = userName?.trim()?.charAt(0)?.toUpperCase() || "S";

    const topNav = [
        { label: "Частным лицам", path: "/personal" },
        { label: "Бизнесу", path: "/business" },
        { label: "Возможности", path: "/features" },
        { label: "Карты", path: "/cards" },
        { label: "Депозит", path: "/deposit" },
    ];

    const cabinetNav = [
        { label: "Главная", route: "/cabinet" },
        { label: "Кошелек", route: WALLET_ROUTE },
        { label: "Мои депозиты", route: "/cabinet/my-deposits" },
        { label: "Партнёры", route: "/cabinet/partners" },
        { label: "Карты", route: "/cabinet/cards" },
    ];

    const isCabinetRouteAvailable = (route) => {
        return isSubscriptionActive || route === WALLET_ROUTE;
    };

    const navigateHandler = (route, isDisabled = false) => {
        if (isDisabled) return;

        navigate(route);
        setIsMobileNav(false);
    };

    const settingsNavigateHandler = () => {
        if (!isSubscriptionActive) return;

        navigateHandler("/cabinet/settings");
    };

    const profileNavigateHandler = () => {
        if (!isSubscriptionActive) return;

        navigateHandler("/cabinet/profile");
    };

    const isCabinetRouteActive = (route) => {
        if (route === "/cabinet") {
            return location.pathname === "/cabinet" || location.pathname === "/cabinet/";
        }

        return location.pathname.startsWith(route);
    };

    const logoutHandler = () => {
        if (!logoutStatusStarted) {
            dispatch(logout());
            setIsMobileNav(false);
        }
    };

    return (
        <header className={Styles.container}>
            <div className={Styles.topBar}>
                <div className={`${Styles.topInner} ${Styles.pageContainer} ${Styles.cabinetPC}`}>
                    <Logo isWhite />

                    <nav className={Styles.desktopNav}>
                        {topNav.map((item) => (
                            <CrossDomainLink
                                key={item.path}
                                path={item.path}
                                className={Styles.topNavItem}
                            >
                                {t(item.label)}
                            </CrossDomainLink>
                        ))}
                    </nav>

                    <div className={Styles.actionsWrap}>
                        <button
                            type="button"
                            className={`
                                ${Styles.desktopSettingsBtn}
                                ${!isSubscriptionActive ? Styles.desktopSettingsBtnDisabled : ""}
                            `}
                            aria-label={t("Настройки")}
                            onClick={settingsNavigateHandler}
                            disabled={!isSubscriptionActive}
                        >
                            <img src={settingsIcon} alt={t("Настройки")} />
                        </button>

                        <div className={Styles.desktopLangWrap}>
                            <LangSwitcher isCabinet={true} />
                        </div>

                        <button
                            type="button"
                            className={`
                                ${Styles.profileBtn}
                                ${!isSubscriptionActive ? Styles.profileBtnDisabled : ""}
                            `}
                            onClick={profileNavigateHandler}
                            disabled={!isSubscriptionActive}
                        >
                            <span className={Styles.avatar}>
                                {avatarSrc ? (
                                    <img src={avatarSrc} alt={userName} className={Styles.avatarImg} />
                                ) : (
                                    <span className={Styles.avatarFallback}>{avatarLetter}</span>
                                )}
                            </span>

                            <span className={Styles.profileName}>{userName}</span>
                        </button>

                        <button
                            type="button"
                            className={Styles.desktopLogOutBtn}
                            aria-label={t("Выйти")}
                            onClick={logoutHandler}
                            disabled={logoutStatusStarted}
                        >
                            <img src={logOutIcon} alt={t("Выйти")} />
                        </button>
                    </div>

                    <button
                        type="button"
                        className={`${Styles.mobileBtn} ${isMobileNav ? Styles.mobileBtnActive : ""}`}
                        onClick={() => setIsMobileNav(prev => !prev)}
                        aria-label={t("Меню")}
                    >
                        <span />
                        <span />
                        <span />
                    </button>
                </div>

                <div className={`${Styles.mobilePanel} ${isMobileNav ? Styles.mobilePanelActive : ""}`}>
                    <nav className={Styles.mobileNav}>
                        {topNav.map((item) => (
                            <CrossDomainLink
                                key={item.path}
                                path={item.path}
                                className={Styles.mobileNavItem}
                                onClick={() => setIsMobileNav(false)}
                            >
                                {t(item.label)}
                            </CrossDomainLink>
                        ))}
                    </nav>

                    <div className={Styles.mobileDivider} />

                    <nav className={Styles.mobileCabinetNav}>
                        {cabinetNav.map((item) => {
                            const isDisabled = !isCabinetRouteAvailable(item.route);

                            return (
                                <button
                                    key={item.route}
                                    type="button"
                                    className={`
                                        ${Styles.mobileCabinetNavItem}
                                        ${isCabinetRouteActive(item.route) ? Styles.mobileCabinetNavItemActive : ""}
                                        ${isDisabled ? Styles.mobileCabinetNavItemDisabled : ""}
                                    `}
                                    onClick={() => navigateHandler(item.route, isDisabled)}
                                    disabled={isDisabled}
                                >
                                    {t(item.label)}
                                </button>
                            );
                        })}
                    </nav>

                    <div className={Styles.mobileDivider} />

                    <div className={Styles.mobileActions}>
                        <button
                            type="button"
                            className={`
                                ${Styles.mobileSettingsBtn}
                                ${!isSubscriptionActive ? Styles.mobileSettingsBtnDisabled : ""}
                            `}
                            aria-label={t("Настройки")}
                            onClick={settingsNavigateHandler}
                            disabled={!isSubscriptionActive}
                        >
                            <img src={settingsIcon} alt={t("Настройки")} />
                        </button>

                        <div className={Styles.mobileLangWrap}>
                            <LangSwitcher isCabinet={true} />
                        </div>

                        <button
                            type="button"
                            className={`
                                ${Styles.mobileProfileBtn}
                                ${!isSubscriptionActive ? Styles.mobileProfileBtnDisabled : ""}
                            `}
                            onClick={settingsNavigateHandler}
                            disabled={!isSubscriptionActive}
                        >
                            <span className={Styles.mobileAvatar}>
                                {avatarSrc ? (
                                    <img src={avatarSrc} alt={userName} className={Styles.mobileAvatarImg} />
                                ) : (
                                    <span className={Styles.mobileAvatarFallback}>{avatarLetter}</span>
                                )}
                            </span>

                            <span className={Styles.mobileProfileName}>{userName}</span>
                        </button>

                        <button
                            type="button"
                            className={Styles.mobileLogOutBtn}
                            aria-label={t("Выйти")}
                            onClick={logoutHandler}
                            disabled={logoutStatusStarted}
                        >
                            <img src={logOutIcon} alt={t("Выйти")} />
                        </button>
                    </div>
                </div>
            </div>

            <div className={Styles.subBar}>
                <div className={`${Styles.subInner} ${Styles.pageContainer} ${Styles.cabinetPC}`}>
                    <nav className={Styles.subNav}>
                        {cabinetNav.map((item) => {
                            const isDisabled = !isCabinetRouteAvailable(item.route);

                            return (
                                <button
                                    key={item.route}
                                    type="button"
                                    className={`
                                        ${Styles.subNavItem}
                                        ${isCabinetRouteActive(item.route) ? Styles.subNavItemActive : ""}
                                        ${isDisabled ? Styles.subNavItemDisabled : ""}
                                    `}
                                    onClick={() => navigateHandler(item.route, isDisabled)}
                                    disabled={isDisabled}
                                >
                                    {t(item.label)}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default CabinetHeader;